import { tr } from "../../../lib/language";
// Wizard step + option enums shared across step components.

export const STEPS = [
  { key: "acknowledge", get label() { return tr("Acknowledgment"); } },
  { key: "start", get label() { return tr("Start"); } },
  { key: "basics", get label() { return tr("Basics"); } },
  { key: "visuals", get label() { return tr("Visuals"); } },
  { key: "option", get label() { return tr("Option & pricing"); } },
  { key: "review", get label() { return tr("Review"); } },
];

export const PRODUCT_TYPES = [
  {
    value: "Tour",
    get label() { return tr("Tour"); },
    get description() { return tr("Guided walking tours, day trips, multi-day trips, city cruises, and similar guided experiences."); },
  },
  {
    value: "Entry Ticket",
    get label() { return tr("Entry Ticket"); },
    get description() { return tr("Access to an attraction, landmark, theme park, show, or event."); },
  },
  {
    value: "City Card",
    get label() { return tr("City Card"); },
    get description() { return tr("A bundled pass covering multiple attractions and/or transport across a city."); },
  },
  {
    value: "Workshop / Class",
    get label() { return tr("Workshop / Class"); },
    get description() { return tr("Hands-on experiences such as cooking classes, creative workshops, and skill-based sessions."); },
  },
  {
    value: "Hop-on Hop-off Ticket",
    get label() { return tr("Hop-on Hop-off Ticket"); },
    get description() { return tr("Entry to a hop-on hop-off bus or boat service."); },
  },
  {
    value: "Transport Experience",
    get label() { return tr("Transport Experience"); },
    get description() { return tr("Scenic or memorable ways to travel — e.g., a heritage train, ferry, or cable car. Standard transfers are not eligible."); },
  },
  {
    value: "Rental Experience",
    get label() { return tr("Rental Experience"); },
    get description() { return tr("Unique rentals such as traditional costumes, bicycles, or vintage cars. Standard vehicle rentals are not eligible."); },
  },
  {
    value: "Accommodation",
    get label() { return tr("Accommodation"); },
    get description() { return tr("Short-term stays in private or shared spaces."); },
  },
  {
    value: "Venue",
    get label() { return tr("Venue"); },
    get description() { return tr("Reserve a court, pitch, or stadium for sports such as tennis, padel, cricket, hockey, and more."); },
  },
  {
    value: "Other",
    get label() { return tr("Other"); },
    get description() { return tr("Bundled offerings that don't fit the above — e.g., multi-attraction packages or combo deals."); },
  },
];

export const START_FROM = ["Blank", "Duplicate existing product"];

export const TITLE_MIN = 10;
export const TITLE_MAX = 60;
export const SHORT_DESC_MIN = 40;
export const SHORT_DESC_MAX = 200;
export const FULL_DESC_MIN = 500;
export const FULL_DESC_MAX = 3000;
export const HIGHLIGHT_COUNT = 3;
export const HIGHLIGHT_MIN = 20;
export const HIGHLIGHT_MAX = 80;
export const VISIT_LOCATIONS_MIN = 1;
export const VISIT_LOCATIONS_MAX = 15;
export const KEYWORDS_MAX = 5;
export const PRODUCT_INTERESTS = [
  "Arts",
  "Culture & Heritage",
  "Food & Drink",
  "Sightseeing & Local Discovery",
  "Attractions & Entertainment",
  "Nature & Landscapes",
  "Wildlife & Animals",
  "Adventure & Outdoors",
  "Water Activities & Cruises",
  "Sports & Recreation",
  "Wellness & Spirituality",
  "Learning, Crafts & Workshops",
  "Nightlife & Social Experiences",
  "Shopping & Lifestyle",
  "Scenic & Heritage Transport",
  "Stays & Getaways",
  "Festivals & Events",
  "Community & Responsible Travel",
];
export const INCLUSIONS_TEXT_MAX = 500;

export const DURATION_TYPES = ["Hours", "Multiple days"];

export const PRIMARY_CATEGORIES = [
  {
    value: "Walking Tours",
    get label() { return tr("Walking Tours"); },
    get description() { return tr("On-foot guided tours through cities, neighbourhoods, monuments or historic sites."); },
  },
  {
    value: "Food Tours",
    get label() { return tr("Food Tours"); },
    get description() { return tr("Tasting walks, market visits, street-food crawls, wine and cocktail tours."); },
  },
  {
    value: "Adventure",
    get label() { return tr("Adventure"); },
    get description() { return tr("Outdoor activities such as hiking, climbing, kayaking, rafting, paragliding or off-road excursions."); },
  },
  {
    value: "Boat Tours",
    get label() { return tr("Boat Tours"); },
    get description() { return tr("Cruises, sailing trips, ferry excursions, sunset cruises, river or lake tours."); },
  },
  {
    value: "Cycling Tours",
    get label() { return tr("Cycling Tours"); },
    get description() { return tr("Guided rides on regular, electric or mountain bikes through city streets, countryside or trails."); },
  },
  {
    value: "Cultural & Specialty Tours",
    get label() { return tr("Cultural & Specialty Tours"); },
    get description() { return tr("Museum visits, art and architecture tours, photography walks, religious or themed cultural experiences."); },
  },
];

export const MAIN_INCLUSIONS = [
  "Professional guide",
  "Transportation",
  "Food & drinks",
  "Entrance tickets",
  "Equipment",
  "Hotel pickup",
  "Wi-Fi onboard",
  "Souvenir / gift",
];

export const NOT_INCLUDED_OPTIONS = [
  "Gratuities",
  "Personal expenses",
  "Travel insurance",
  "Hotel pickup",
  "Food and drinks",
  "Entrance fees",
  "Transportation",
];

export const MEAL_TYPES = ["Breakfast", "Brunch", "Lunch", "Snack", "Dinner", "Drinks"];
export const MEAL_FORMATS = [
  "Full meal",
  "Set menu",
  "Tasting menu",
  "Food tasting",
  "Drinks only",
  "Cooking class",
];

export const TRANSPORT_TYPES = [
  "Air-conditioned bus",
  "Mini-van",
  "Private car",
  "Boat / ferry",
  "Bicycle",
  "Cable car / tram",
  "On foot",
  "Train",
];

export const NOT_SUITABLE_FOR = [
  "Pregnant women",
  "Wheelchair users",
  "Travelers with limited mobility",
  "Children under 5",
  "People with heart conditions",
  "People with back injuries",
  "Travelers with severe allergies",
];

export const SUITABILITY_TAGS = [
  "Women-only",
  "LGBTQ+ friendly",
  "Pet friendly",
  "Service animals welcome",
  "Family-friendly",
  "Solo-traveler friendly",
  "Senior friendly",
  "Small group",
  "Private experience",
];

export const PRICING_PER_CATEGORY_MODES = [
  { value: "flat", get label() { return tr("Same for everyone"); } },
  { value: "per_category", get label() { return tr("Depends on category"); } },
];

export const PRICE_CATEGORY_DEFS = [
  { key: "adult", get label() { return tr("Adult"); }, required: true, default_age_range: "13+" },
  { key: "child", get label() { return tr("Child"); }, required: false, default_age_range: "3–12" },
  { key: "infant", get label() { return tr("Infant"); }, required: false, default_age_range: "0–2" },
];

export const MEETING_POINT_TYPES = [
  { value: "set_meeting_point", get label() { return tr("Customers go to a set meeting point"); } },
  { value: "pickup", get label() { return tr("Customers get picked up"); } },
];

export const PRIVACY_TYPES = [
  {
    value: "Shared group",
    get label() { return tr("Shared group"); },
    get description() { return tr("Customers may share the activity with strangers"); },
  },
  {
    value: "Private",
    get label() { return tr("Private"); },
    get description() { return tr("Only the customer's own group joins."); },
  },
];

export const OPTION_DURATION_UNITS = ["Minutes", "Hours", "Days"];
export const REFUND_POLICY_OPTIONS = [
  {
    value: "standard_24_hours",
    get label() { return tr("Standard 24-hours refund policy (recommended)"); },
    get description() { return tr("Customers can cancel up to 24 hours before the activity start time."); },
  },
  {
    value: "not_refundable",
    get label() { return tr("Not refundable"); },
    get description() { return tr("Bookings for this product cannot be refunded after purchase."); },
  },
  {
    value: "custom",
    get label() { return tr("Custom refund policy"); },
    get description() { return tr("Set your own cancellation window before the activity start time."); },
  },
];
export const REFUND_POLICY_UNITS = ["Minute(s)", "Hour(s)", "Day(s)"];

/* Major world languages surfaced on the Option editor. Suppliers can pick "Other"
 * to type free-text languages. */
export const GUIDE_LANGUAGES = [
  "Not Applicable",
  "Arabic",
  "Bengali",
  "Bulgarian",
  "Burmese",
  "Cantonese",
  "Chinese",
  "Croatian",
  "Czech",
  "Danish",
  "Dutch",
  "English",
  "Estonian",
  "Filipino",
  "Finnish",
  "French",
  "German",
  "Greek",
  "Gujarati",
  "Hebrew",
  "Hindi",
  "Hungarian",
  "Indonesian",
  "Italian",
  "Japanese",
  "Kannada",
  "Korean",
  "Malay",
  "Malayalam",
  "Mandarin",
  "Marathi",
  "Norwegian",
  "Persian",
  "Polish",
  "Portuguese",
  "Punjabi",
  "Romanian",
  "Russian",
  "Serbian",
  "Spanish",
  "Swahili",
  "Swedish",
  "Tamil",
  "Telugu",
  "Thai",
  "Turkish",
  "Ukrainian",
  "Urdu",
  "Vietnamese",
];

/* Duration vs Validity radio choices for the Option editor. */
export const DURATION_OR_VALIDITY = [
  {
    value: "duration",
    get label() { return tr("It lasts for a specific duration"); },
    get description() { return tr("Example: 3 hour walking tour."); },
  },
  {
    value: "validity",
    get label() { return tr("Ticket is valid anytime during a certain period"); },
    get description() { return tr("Example: Museum ticket that can be used any time during opening hours."); },
  },
];

/* Time slots vs Opening hours availability modes for the Option editor. */
export const AVAILABILITY_MODES = [
  {
    value: "time_slots",
    get label() { return tr("Time slots"); },
    get description() { return tr("Example: City tour starting at 10:00 AM, 11:00 AM, and 5:00 PM."); },
  },
  {
    value: "opening_hours",
    get label() { return tr("Opening hours"); },
    get description() { return tr("Example: Museum open from Mon to Friday, between 10:00 AM and 5:00 PM."); },
  },
];

/* Pickup transport modes for the Option editor.
 * `isHeader: true` rows render as non-clickable group labels in SelectField. */
export const PICKUP_TRANSPORT_MODES = [
  { isHeader: true, get label() { return tr("2-wheeler"); } },
  { value: "Motorcycle", get label() { return tr("Motorcycle"); } },
  { value: "Scooter", get label() { return tr("Scooter"); } },
  { value: "Bicycle", get label() { return tr("Bicycle"); } },
  { value: "Segway", get label() { return tr("Segway"); } },
  { isHeader: true, get label() { return tr("Car / Bus"); } },
  { value: "Car", get label() { return tr("Car"); } },
  { value: "Limousine", get label() { return tr("Limousine"); } },
  { value: "SUV / Jeep", get label() { return tr("SUV / Jeep"); } },
  { value: "Taxi", get label() { return tr("Taxi"); } },
  { value: "Van", get label() { return tr("Van"); } },
  { value: "Minivan", get label() { return tr("Minivan"); } },
  { value: "Bus / Coach", get label() { return tr("Bus / Coach"); } },
  { isHeader: true, get label() { return tr("Water"); } },
  { value: "Boat", get label() { return tr("Boat"); } },
  { value: "Ferry", get label() { return tr("Ferry"); } },
  { value: "Gondola", get label() { return tr("Gondola"); } },
  { value: "Other water transport", get label() { return tr("Other water transport"); } },
  { isHeader: true, get label() { return tr("Air"); } },
  { value: "Helicopter", get label() { return tr("Helicopter"); } },
  { isHeader: true, get label() { return tr("Other"); } },
  { value: "Train", get label() { return tr("Train"); } },
  { value: "Tram", get label() { return tr("Tram"); } },
  { value: "Tuk-tuk", get label() { return tr("Tuk-tuk"); } },
  { value: "Golf cart", get label() { return tr("Golf cart"); } },
  { value: "On foot", get label() { return tr("On foot"); } },
];

/* Sub-types when "validity" is picked. */
export const VALIDITY_TYPES = [
  {
    value: "from_booking",
    get label() { return tr("Valid for a period of time from date booked"); },
  },
  {
    value: "from_activation",
    get label() { return tr("Valid for a period of time from first activation"); },
  },
  {
    value: "day_booked",
    get label() { return tr("Valid on the day booked"); },
  },
];

export const WEEKDAYS = [
  { key: "mon", get label() { return tr("Mon"); } },
  { key: "tue", get label() { return tr("Tue"); } },
  { key: "wed", get label() { return tr("Wed"); } },
  { key: "thu", get label() { return tr("Thu"); } },
  { key: "fri", get label() { return tr("Fri"); } },
  { key: "sat", get label() { return tr("Sat"); } },
  { key: "sun", get label() { return tr("Sun"); } },
];

export const CUTOFF_OPTIONS = (() => {
  const out = [];
  for (let m = 0; m <= 60; m += 5) {
    out.push({ value: m, label: m === 0 ? "No cutoff (book up to start)" : `${m} minutes` });
  }
  for (let h = 2; h <= 10; h += 1) {
    out.push({ value: h * 60, label: `${h} hours` });
  }
  return out;
})();

export const INTERVAL_OPTIONS = (() => {
  const out = [];
  for (let m = 5; m <= 240; m += 5) out.push(m);
  return out;
})();

export const EXPERIENCE_LEADERS = [
  {
    value: "Self-Guided",
    get label() { return tr("Self-Guided"); },
    get description() { return tr("No guide is provided — travelers explore the activity or attraction entirely on their own."); },
  },
  {
    value: "Tour Guide",
    get label() { return tr("Tour Guide"); },
    get description() { return tr("A guide leads the group, providing commentary and context throughout the experience."); },
  },
  {
    value: "Host or Greeter",
    get label() { return tr("Host or Greeter"); },
    get description() { return tr("Assists with introductions, ticketing, or queue management — but does not deliver a full guided tour."); },
  },
  {
    value: "Instructor",
    get label() { return tr("Instructor"); },
    get description() { return tr("Teaches participants how to use equipment or complete a skill-based activity."); },
  },
  {
    value: "Driver",
    get label() { return tr("Driver"); },
    get description() { return tr("Transports customers to a destination without providing commentary or narration."); },
  },
];

export const PHOTO_MIN_COUNT = 4;
export const PHOTO_MAX_COUNT = 10;
export const PHOTO_MIN_WIDTH = 1280;
export const PHOTO_MIN_HEIGHT = 960;
export const PHOTO_ACCEPTED = ["image/jpeg", "image/png", "image/webp"];
export const PHOTO_MAX_BYTES = 20 * 1024 * 1024;

// Currency list used by the Legal insurance form.
export const TICKET_CURRENCIES = [
  "USD",
  "EUR",
  "GBP",
  "JPY",
  "CAD",
  "AUD",
  "INR",
  "ZAR",
  "AED",
  "BRL",
  "CHF",
  "CNY",
  "MXN",
  "NZD",
  "SGD",
];


export function createBlankOption() {
  // Every radio / select / multi-choice field starts blank so the supplier
  // explicitly picks each one.
  return {
    name: "",
    reference_code: "",
    languages: [],
    languages_other: "",
    privacy_type: "",
    // "duration" → uses duration_unit + duration_value
    // "validity" → uses validity_type + (when not "day_booked") duration_unit + duration_value
    duration_or_validity: "",
    validity_type: "",
    duration_unit: "Hours",
    duration_value: "",
    meeting_point_type: "",
    meeting_point_location: "",
    meeting_point_coords: null, // {lat, lng} | null — captured from Photon on pick
    meeting_point_description: "",
    pickup_area_description: "",
    pickup_transport_mode: "",
    schedule_start_date: "",
    has_schedule_end_date: false,
    schedule_end_date: "",
    availability_mode: "",
    schedule_slots: [],
    cutoff_minutes: 60,

    // ---- Pricing ----
    // pricing_basis: "" | "per_person" | "per_group"
    pricing_basis: "",
    // pricing_mode: "" | "flat" | "per_category" — only meaningful for per_person
    pricing_mode: "",
    // Capacity per time slot (replaces old min/max participants fields).
    capacity_min: 1,
    capacity_max: "",
    // Per-person flat tiers — ordered list of qty ranges with a price each.
    // First tier's `from` is always 1; subsequent tiers' `from` derives from
    // previous `to + 1` (read-only display).
    flat_tiers: [{ to: "", price: "" }],
    // Per-person per-category tiers. Categories are ordered: optional
    // infant first, then child, then adult (always last). Adult age range
    // is open-ended on the high end.
    categories: [
      { key: "child", enabled: true, age_max: 12, tiers: [{ to: "", price: "" }] },
      { key: "adult", enabled: true, age_max: null, tiers: [{ to: "", price: "" }] },
    ],
    // Per group / vehicle: ranges of group sizes. Each row has `to` editable;
    // `from` derives from previous row's `to + 1`. Prices are keyed by the
    // upper bound of each range so they survive reorders.
    group_sizes: [{ to: "" }],
    group_prices: {}, // { [upper_bound]: price }
  };
}

export const initialFormState = () => ({
  product_type: "",
  // Creation-only acknowledgments — both must be true to leave step 0.
  acknowledged_authorization: false,
  acknowledged_compliance: false,
  // Kept for backend compatibility but no longer surfaced in the UI.
  start_from: "Blank",
  duplicate_source_id: "",
  title: "",
  reference_code: "",
  primary_city: "",
  short_description: "",
  full_description: "",
  highlights: ["", "", ""],
  visit_locations: [],
  keywords: [],
  experience_leader: "",
  inclusions_tags: [],
  inclusions_other: "",
  not_included_tags: [],
  not_included_other: "",
  meals: [],
  transports: [],
  not_suitable_for: [],
  suitability_tags: [],
  guest_must_know: "",
  refund_policy_type: "standard_24_hours",
  custom_refund_policy_value: "",
  custom_refund_policy_unit: "Hour(s)",
  photos: [],
  copyright_confirmed: false,
  options: [],
  compliance_certified: false,
});
